// A workspace's Jira and Slack connections, on the settings surface (#492), and its
// webhook receivers (#1073), registered and edited through `WebhookFlow.tsx`.
//
// The list, and a way to connect each kind, each through a stepped flow
// (`JiraFlow.tsx`, #599; `SlackFlow.tsx`, #601). The credential is typed once and sent
// once: the form clears it on success and no read brings it back, so the list
// can only say that one is held. Sending is each adapter's (#265, #266); this
// section is where the workspace gives them somewhere to send.
//
// **The words are Jira's and Slack's own** (#578). Jira Cloud renamed projects
// to spaces and issues to work items during 2025, so this form says space key
// and work type while `project_key` and `issue_type` stay on the wire; Slack's
// own name for the `xoxb-` token is its Bot User OAuth Token. The API field
// names do not change — only what a person reads.
//
// The three checks this form makes before it posts are three refusals the live
// proofs of #265 and #266 actually hit: a Jira page URL pasted whole, a channel
// name where Slack wants an id, and a token that is not a bot token. Each says
// what was wrong, because a form that only goes red teaches nothing.
//
// **A credential field is not a sign-in field** (#571). Chrome and most password
// managers ignore `autocomplete="off"` on a password input and fill a saved
// password into it — on 2026-09-17 a Jira connection saved from a pasted token held
// a different string, and Jira answered 401 for every delivery after. So each secret
// carries `autoComplete="new-password"`, which browsers do honour, and a `name` no
// sign-in form uses; the email beside it is named too, so the pair does not read as
// a login. Each secret also reveals on demand, because a person cannot check what is
// about to be sealed if they cannot read it back.
//
// **One flow per kind, connecting or editing** (#580, #817). Edit reopens the kind's
// flow at its choose step with the connection's settings in place, so a saved space,
// work type or channel is changed from the same live lists it was chosen from, and not
// typed by hand. `jiraEdit` and `slackEdit` below are what an edit sends.
//
// What editing changes is what leaves the browser. A connecting flow sends
// everything; an editing one sends `config` only when a setting actually moved and
// `credential` only when the token was replaced, because the server keeps whatever
// key is absent (`integrations/setup.py`, `update`, `coalesce`). So the token a
// person is not replacing is never re-sent — not as a placeholder, not as the string
// a browser refilled, not at all.

import { useEffect, useRef, useState } from "react";

import { messageOf } from "../api/http";
import {
  RETRYABLE,
  SEVERITY_LABEL,
  SLACK_CHANNEL_ID,
  targetOf,
  webhookDeliveryEvent,
  webhookEventInWords,
  type AdapterKind,
  type CreatedWebhook,
  type RotatedWebhook,
  type DeliveryRow,
  type IntegrationKind,
  type IntegrationPatch,
  type IntegrationRow,
  type JiraConfig,
  type JiraPatch,
  type NewIntegration,
  type NewPastedSlack,
  type Severity,
  type SlackConfig,
  type SlackPatch,
  type WebhookConfig,
} from "../api/integrations";
import type { JiraLister } from "../api/jiraLists";
import type { SlackLister } from "../api/slackChannels";
import { reinstallHref, type SlackInstallResult } from "../api/slackInstall";
import { runPagePath } from "../runId";
import { Chip, Notice } from "./chrome";
import {
  deliveryInWords,
  deliveryLink,
  grantInWords,
  healthInWords,
  missingGrant,
  statusOf,
} from "./connectionHealth";
import { AnswerUnread, CHECK_NOT_ANSWERED, enabledInWords, sentTestInWords, testedInWords, unreadInWords } from "./connectionTest";
import { withoutConnectRequest } from "./integrationsStep";
import { jiraIntegration, type JiraFields } from "./jiraFields";
import { JiraFlow } from "./JiraFlow";
import { readableTime } from "./runPresentation";
import { reinstalledInWords, SlackFlow, type HeldInstallation } from "./SlackFlow";
import type { StepUp } from "../api/policy";
import type { BeginPasskey, StepUpFactors } from "./StepUp";
import { ConnectWebhook, EditWebhook, RotateWebhookSecret } from "./WebhookFlow";

// The Jira settings are asked for by the flow, so they live in `jiraFields.tsx`; whatever
// read them from this module still does.
export { jiraIntegration, jiraSiteAddress, type JiraFields, type SiteAddress } from "./jiraFields";

const KIND_LABEL: Record<IntegrationKind, string> = { jira: "Jira Cloud", slack: "Slack", webhook: "Webhook" };

/**
 * The kinds that have a check, and so a Test connection (#603): `setup.CHECKED_KINDS`. A
 * webhook receiver has none; it has Send test event instead (#1076). A `string` set, because
 * the list answers every kind the server holds, whatever this module's types name.
 */
const TESTABLE: ReadonlySet<string> = new Set<IntegrationKind>(["jira", "slack"]);

/**
 * The kinds a disconnected connection can be reconnected from (#820): the checked kinds, which
 * are checked first, and a webhook receiver, which is sent its test event and goes live only
 * on a 2xx answer (#1076).
 */
const RECONNECTABLE: ReadonlySet<string> = new Set<IntegrationKind>(["jira", "slack", "webhook"]);

/**
 * The installation a Slack connection posts through, when Reinstall can replace it (#938):
 * where a pasted connection's token is replaced (#580), an installation is reinstalled.
 */
export const reinstallable = (row: IntegrationRow): string | null => row.installation_id ?? null;

/**
 * An installation as this workspace's connections know it (#938): the Slack workspace's name
 * from any of them, and how many live ones post through it.
 */
export function heldBy(rows: IntegrationRow[] | null, installationId: string): HeldInstallation {
  const through = (rows ?? []).filter((row) => row.installation_id === installationId);
  // The name is the installation's, so every connection through it carries the same one.
  return { name: through[0]?.team_name ?? null, connections: through.filter((row) => row.active).length };
}

/** Which flows start open: none, or the one the location asked for (#602). */
export const addingFor = (connect: AdapterKind | null): Record<IntegrationKind, boolean> => ({
  jira: connect === "jira",
  slack: connect === "slack",
  webhook: false,
});

/**
 * What a person is told before a connection stops. It can be reconnected, which checks it
 * first (#820), so the warning says what stops and not that it is final.
 */
export const DISCONNECT_WARNING =
  "Nothing more is sent here until it is reconnected. Reconnecting checks the connection first.";

/** What a person is told before a connection and its saved token go for good (#820). */
export const DELETE_WARNING =
  "Deleting removes this connection and its saved token. This cannot be undone; sending here again means a new connection.";

/** What is wrong with a Slack channel id, or null when nothing is (#578). */
export function slackChannelProblem(raw: string): string | null {
  const typed = raw.trim();
  if (typed === "") return null;
  if (SLACK_CHANNEL_ID.test(typed.toUpperCase())) return null;
  if (typed.startsWith("#")) {
    return `Slack needs the channel’s ID, not its name. In Slack, open ${typed} → its name → About → Channel ID; it starts with C.`;
  }
  return `“${typed}” is not a channel ID. An ID is a C — or G for a private channel — then capitals and digits, like C0123456789. In Slack: open the channel → its name → About → Channel ID.`;
}

/** What is wrong with a Slack bot token, or null when nothing is. Never echoes the token. */
export function slackTokenProblem(raw: string): string | null {
  const typed = raw.trim();
  if (typed === "") return null;
  if (typed.startsWith("xoxb-")) return null;
  const dash = typed.indexOf("-");
  const starts = dash <= 0 ? "" : `, and this one starts with ${typed.slice(0, dash + 1)}`;
  return `A Bot User OAuth Token starts with xoxb-${starts}. In your Slack app: OAuth & Permissions → Bot User OAuth Token.`;
}

/** What a Slack form holds, connecting or editing. */
export interface SlackFields {
  channel: string;
  floor: Severity;
  token: string;
}

/** The channel as it is sent. An id is upper-cased, because Slack's own are. */
export function slackChannelSent(raw: string): string {
  const channel = raw.trim();
  return SLACK_CHANNEL_ID.test(channel.toUpperCase()) ? channel.toUpperCase() : channel;
}

/** What the Slack form sends: your own app's, with its pasted token. */
export function slackIntegration(fields: SlackFields): NewPastedSlack {
  return {
    kind: "slack",
    config: {
      channel: slackChannelSent(fields.channel),
      severity_floor: fields.floor,
    },
    credential: { bot_token: fields.token },
  };
}

/**
 * An edit read off a form: what to send, and what to tell the person was done (#580).
 *
 * The two are built together and from the same comparison, so the sentence a person
 * reads after saving cannot claim a change the body did not carry, or stay silent
 * about one it did. `changes` empty is the whole "nothing has changed" test: an edit
 * with nothing in it has nothing to say and nothing to send.
 */
export interface Edit<P extends IntegrationPatch> {
  patch: P;
  changes: string[];
}

const sameLabels = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((label, at) => label === b[at]);

/** The token, when the person typed one. An untouched field replaces nothing. */
const replacing = (token: string): boolean => token.trim() !== "";

/** What a replaced credential is called, whichever kind holds it. */
const TOKEN_REPLACED = "Token replaced";

/** What a Jira edit sends, and what it says it did. */
export function jiraEdit(current: JiraConfig, fields: JiraFields): Edit<JiraPatch> {
  const whole = jiraIntegration(fields);
  const next = whole.config;
  const changes: string[] = [];
  if (next.base_url !== current.base_url) changes.push(`Jira site address changed to ${next.base_url}`);
  if (next.project_key !== current.project_key) changes.push(`Space key changed to ${next.project_key}`);
  if (next.issue_type !== current.issue_type) changes.push(`Work type changed to ${next.issue_type}`);
  if (!sameLabels(next.labels, current.labels)) {
    changes.push(next.labels.length === 0 ? "Labels cleared" : `Labels changed to ${next.labels.join(", ")}`);
  }
  const patch: JiraPatch = {};
  if (changes.length > 0) patch.config = next;
  if (replacing(fields.token)) {
    patch.credential = whole.credential;
    changes.push(TOKEN_REPLACED);
  }
  return { patch, changes };
}

/** What a Slack edit sends, and what it says it did. */
export function slackEdit(current: SlackConfig, fields: SlackFields): Edit<SlackPatch> {
  const whole = slackIntegration(fields);
  const next = whole.config;
  const changes: string[] = [];
  if (next.channel !== current.channel) changes.push(`Channel ID changed to ${next.channel}`);
  if (next.severity_floor !== current.severity_floor) {
    changes.push(`Conclusions now post at ${SEVERITY_LABEL[next.severity_floor]} and above`);
  }
  const patch: SlackPatch = {};
  if (changes.length > 0) patch.config = next;
  if (replacing(fields.token)) {
    patch.credential = whole.credential;
    changes.push(TOKEN_REPLACED);
  }
  return { patch, changes };
}

/**
 * One connection's health, and its deliveries when a person opens them (#579).
 *
 * The line comes from `connectionHealth.ts` and is always rendered: the failure this
 * ticket exists for was readable only from a private cluster's log stream, and a person
 * who has to expand a row to learn a connection is broken will not. Its chip is the
 * Status column's, one status for the row (#798).
 */
function Health({
  row,
  deliveries,
  onExpand,
  onRetry,
  mayEdit,
}: {
  row: IntegrationRow;
  deliveries: DeliveryRow[] | null | string | undefined;
  onExpand: ((id: string) => Promise<void>) | undefined;
  /** Retry one of this connection's deliveries (#819): only where this person may, on a live row. */
  onRetry: ((deliveryId: string) => Promise<void>) | undefined;
  /** This person has the Edit button that repairs a missing permission (#605). */
  mayEdit: boolean;
}) {
  return (
    <div className="stack stack--tight">
      <small className="field__hint">{healthInWords(row)}</small>
      {row.active && missingGrant(row) !== null ? (
        <small className="field__hint" data-testid={`grant-${row.id}`}>
          {grantInWords(row, mayEdit)}
        </small>
      ) : null}
      {row.last_check === null || row.last_check === undefined ? null : (
        <small className="field__hint">
          Last checked <time dateTime={row.last_check.checked_at}>{readableTime(row.last_check.checked_at)}</time>
        </small>
      )}
      {row.last_delivery === null ? null : (
        <small className="field__hint">
          Last attempt <time dateTime={row.last_delivery.updated_at}>{readableTime(row.last_delivery.updated_at)}</time>
        </small>
      )}
      {onExpand === undefined ? null : (
        <details
          data-testid={`deliveries-${row.id}`}
          open={Array.isArray(deliveries)}
          onToggle={(event) => {
            if (event.currentTarget.open) void onExpand(row.id);
          }}
        >
          <summary>Recent deliveries</summary>
          <Deliveries kind={row.kind} deliveries={deliveries} onRetry={onRetry} />
        </details>
      )}
    </div>
  );
}

function Deliveries({
  kind,
  deliveries,
  onRetry,
}: {
  kind: IntegrationKind;
  deliveries: DeliveryRow[] | null | string | undefined;
  onRetry: ((deliveryId: string) => Promise<void>) | undefined;
}) {
  // Per delivery: a retry in flight, and a refusal to show under that row (#819). A
  // retried row comes back `pending` from the route, so its button goes with it.
  const [retrying, setRetrying] = useState<Record<string, true>>({});
  const [refused, setRefused] = useState<Record<string, string>>({});
  const retry = async (id: string) => {
    if (onRetry === undefined) return;
    setRetrying((was) => ({ ...was, [id]: true }));
    setRefused(({ [id]: _gone, ...rest }) => rest);
    try {
      await onRetry(id);
    } catch (refusal) {
      setRefused((was) => ({ ...was, [id]: `${messageOf(refusal)} The delivery was not retried.` }));
    } finally {
      setRetrying(({ [id]: _gone, ...rest }) => rest);
    }
  };
  // Not asked for yet, and asked for but still in flight, are different things: the
  // first is what a closed row holds, and saying "reading…" there would be a lie the
  // person never sees but a test would pin.
  if (deliveries === undefined) return <p className="muted">Open this to read what has been sent.</p>;
  if (deliveries === null) return <p className="muted">Reading this connection's deliveries…</p>;
  if (typeof deliveries === "string") return <Notice tone="bad">{deliveries}</Notice>;
  if (deliveries.length === 0) return <p className="muted">Nothing has been sent to this connection yet.</p>;
  return (
    <ul className="stack stack--tight">
      {deliveries.map((delivery) => {
        const link = deliveryLink(kind, delivery);
        return (
          <li key={delivery.id}>
            <DeliveryEvent kind={kind} delivery={delivery} />{" "}
            <time dateTime={delivery.updated_at}>{readableTime(delivery.updated_at)}</time>
            <br />
            <small className="field__hint">{deliveryInWords(kind, delivery)}</small>
            {link === null ? null : (
              <>
                {" "}
                <a href={link.href} target="_blank" rel="noreferrer noopener">
                  Open {link.text}
                </a>
              </>
            )}
            {typeof delivery.run_id === "string" && delivery.run_id !== "" ? (
              <>
                {" "}
                <a href={runPagePath(delivery.run_id)}>Open the investigation</a>
              </>
            ) : null}
            {onRetry === undefined || !RETRYABLE.includes(delivery.status) ? null : (
              <>
                {" "}
                <button type="button" className="btn btn--small" disabled={retrying[delivery.id] === true} onClick={() => void retry(delivery.id)}>
                  {retrying[delivery.id] === true ? "Retrying…" : "Retry"}
                </button>
              </>
            )}
            {refused[delivery.id] === undefined ? null : (
              <>
                <br />
                <small className="field__hint" role="alert">
                  {refused[delivery.id]}
                </small>
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * What a delivery carried. A webhook's says the event as the receiver was sent it (#1073):
 * its words and its public name; an adapter's, the event in the server's words (#579).
 */
function DeliveryEvent({ kind, delivery }: { kind: IntegrationKind; delivery: DeliveryRow }) {
  const sent = kind === "webhook" ? webhookDeliveryEvent(delivery) : null;
  if (sent === null) return <strong>{delivery.event ?? "delivery"}</strong>;
  return (
    <>
      <strong>{sent.words}</strong> <code>{sent.wire}</code>
    </>
  );
}

/** Where a row sends, as a person reads it: a webhook's URL and what it is sent (#1073). */
function SendsTo({ row }: { row: IntegrationRow }) {
  if (row.kind !== "webhook") return <code className="break">{targetOf(row)}</code>;
  const events = Array.isArray(row.config.events) ? row.config.events : [];
  return (
    <div className="stack stack--tight">
      <code className="break">{targetOf(row)}</code>
      <small className="field__hint">{events.length === 0 ? "No events chosen." : `Sent: ${events.map(webhookEventInWords).join(", ")}`}</small>
    </div>
  );
}

/** What the row says of its credential, never the credential. */
function CredentialCell({ row }: { row: IntegrationRow }) {
  if (row.kind === "webhook") {
    const held =
      typeof row.secret_prefix === "string" && row.secret_prefix !== "" ? (
        <code>{row.secret_prefix}…</code>
      ) : row.has_credential ? (
        <>typed secret</>
      ) : (
        <span className="muted">Missing</span>
      );
    if (row.previous_secret_until == null) return held;
    // A rotation's overlap (#1075): the secret it replaced still signs beside this one.
    return (
      <div className="stack stack--tight">
        {held}
        <small className="field__hint">
          Previous secret also signs until <time dateTime={row.previous_secret_until}>{readableTime(row.previous_secret_until)}</time>
        </small>
      </div>
    );
  }
  if (row.credential_kind === "installation") return <>Added to Slack</>;
  return row.has_credential ? <>Saved</> : <span className="muted">Missing</span>;
}

export function Integrations({
  integrations,
  deliveries,
  error,
  mayManage,
  connect = null,
  slackInstall = null,
  onCreate,
  onDisable,
  onEnable,
  onDelete,
  onUpdate,
  onExpand,
  onRetry,
  onTest,
  onAwaitCheck,
  onSendTest,
  onSendTestEvent,
  onListJira,
  onListSlack,
  onRegisterWebhook,
  onRotateWebhook,
  stepUpFactors,
  beginPasskey,
}: {
  /** The workspace's connections, or null while the read is in flight. */
  integrations: IntegrationRow[] | null;
  /** Each expanded connection's deliveries, by id (#579). */
  deliveries?: Record<string, DeliveryRow[] | null | string> | null;
  error: string | null;
  mayManage: boolean;
  /**
   * The flow the location asked for (#602): the same flow its Connect button opens,
   * opened once. It seeds `adding` and is never read again, so closing the flow closes it.
   */
  connect?: AdapterKind | null;
  /**
   * What Slack's install callback said, when the person has just come back from Add to Slack
   * (#937): the Connect Slack flow resumes from it. Read once, as `connect` is.
   */
  slackInstall?: SlackInstallResult | null;
  onCreate: (body: NewIntegration) => Promise<IntegrationRow | void>;
  onDisable: (id: string) => Promise<void>;
  /** Reconnect a disconnected connection (#820): the row carrying its check's answer, or null. */
  onEnable?: (id: string) => Promise<IntegrationRow | null>;
  /** Remove a connection's saved token, and the connection from the list (#820). */
  onDelete?: (id: string) => Promise<void>;
  /** Change a live connection instead of re-creating it (#580). The row as saved, where known. */
  onUpdate?: (id: string, body: IntegrationPatch) => Promise<IntegrationRow | void>;
  onExpand?: (id: string) => Promise<void>;
  /** Put one failed or abandoned delivery back on the queue (#819); rejects with the refusal. */
  onRetry?: (connectionId: string, deliveryId: string) => Promise<void>;
  /** Test a connection with the credential it holds (#603): the row with the answer, or null. */
  onTest?: (id: string) => Promise<IntegrationRow | null>;
  /**
   * Wait for the check a save has just started (#615): the row with its answer, or null when
   * none arrived. The same wait a test makes, with nothing posted (`connectionTest.ts`).
   */
  onAwaitCheck?: (id: string) => Promise<IntegrationRow | null>;
  /** Send one test work item or message from a connection a flow has just saved (#614, #601). */
  onSendTest?: (id: string) => Promise<void>;
  /**
   * Send a webhook receiver its test event (#1076): the row carrying the receiver's answer, or
   * null when none arrived. Absent, Send test event is not offered.
   */
  onSendTestEvent?: (id: string) => Promise<IntegrationRow | null>;
  /** Read Jira's spaces and work types for the flow's choose step (#600). */
  onListJira?: JiraLister;
  /** Read Slack's public channels for the flow's choose step (#604). */
  onListSlack?: SlackLister;
  /**
   * Register a webhook receiver under a `webhook_create` step-up (#1073): the create's answer,
   * the only one that carries the secret. Absent, Connect webhook is not offered.
   */
  onRegisterWebhook?: (config: WebhookConfig, stepUp: StepUp) => Promise<CreatedWebhook>;
  /**
   * Rotate a receiver's secret under a `webhook_rotate` step-up (#1075): the rotation's answer,
   * the only one that carries the new secret. Absent, Rotate secret is not offered.
   */
  onRotateWebhook?: (id: string, stepUp: StepUp) => Promise<RotatedWebhook>;
  /** `GET /auth/mfa`'s factors, for the step-up Connect webhook and Rotate secret ask for. */
  stepUpFactors?: () => Promise<StepUpFactors>;
  /** The passkey challenge for that step-up. */
  beginPasskey?: BeginPasskey;
}) {
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState<Record<string, true>>({});
  const [enabling, setEnabling] = useState<Record<string, true>>({});
  const [sending, setSending] = useState<Record<string, true>>({});
  const [tested, setTested] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  // A Reinstall's return is said on this list, not in a flow (#938).
  const [adding, setAdding] = useState<Record<IntegrationKind, boolean>>(() =>
    addingFor(slackInstall?.word === "replaced" ? null : connect),
  );
  const section = useRef<HTMLElement>(null);
  // Read once, as `adding` is (#781). The request leaves the address in the first effect
  // below, so every later render is handed `connect: null`; the flow can render only once
  // the roles have answered, which on a page load is after that. Read per render, the
  // scroll below had stopped asking by then, and the flow was in view only when its
  // heading's focus happened to land after the sections above it had finished growing.
  const [asked] = useState(() => connect !== null);
  // The flow resumes from the return once; closed, Connect Slack opens it fresh.
  const [returned, setReturned] = useState(slackInstall);

  // The request leaves the address as soon as it is read, so a reload or Back does not
  // re-open a flow the person closed. Whoever may not connect loses it too, and sees
  // what this section shows them anyway.
  useEffect(() => {
    if (!asked) return;
    window.history.replaceState(window.history.state, "", withoutConnectRequest(window.location.pathname, window.location.search));
  }, [asked]);
  // Other sections sit above this one: bring the flow into view once it can render, and
  // again whenever the page around it changes size, until the person moves the page
  // themselves — what a browser does for an address's fragment while a document loads.
  // Once is not enough (#781): those sections fill in as their own reads answer, often
  // after the roles, and the flow is the page's last thing, so the scroll stops at the
  // page's end with the section above still showing. The browser anchors to that
  // section, so when it grows the flow is pushed out of view and nothing brings it back.
  useEffect(() => {
    const flow = asked && mayManage ? section.current?.querySelector(".step-flow") : null;
    if (flow == null) return;
    const bring = () => flow.scrollIntoView({ block: "start" });
    bring();
    const page = section.current?.parentElement;
    if (page == null || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(bring);
    observer.observe(page);
    const moves = ["wheel", "touchstart", "keydown", "pointerdown"] as const;
    const stop = () => {
      observer.disconnect();
      for (const move of moves) window.removeEventListener(move, stop, true);
    };
    for (const move of moves) window.addEventListener(move, stop, { capture: true, passive: true });
    return stop;
  }, [asked, mayManage]);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [rotating, setRotating] = useState<string | null>(null);
  // Each row's Rotate secret, so closing its flow puts focus back where it was opened.
  const rotateButtons = useRef<Record<string, HTMLButtonElement | null>>({});
  const active = integrations?.filter((row) => row.active).length ?? 0;
  const edited = integrations?.find((row) => row.id === editing && row.active) ?? null;
  const rotated = integrations?.find((row) => row.id === rotating && row.active && row.kind === "webhook") ?? null;
  const closeRotation = (id: string) => {
    setRotating(null);
    rotateButtons.current[id]?.focus();
  };

  // One form of a kind at a time. Both forms render the same field ids, so two of them
  // open together put 12 duplicate ids in the document and every `<label for>` in the
  // second binds the first form's input — the labels stop reaching the fields a person
  // is actually typing into. Opening either closes the other.
  const open = (kind: IntegrationKind) => {
    setEditing(null);
    setRotating(null);
    setAdding((was) => ({ ...was, [kind]: true }));
  };
  const close = (kind: IntegrationKind) => {
    setAdding((was) => ({ ...was, [kind]: false }));
    if (kind === "slack") setReturned(null);
  };
  const without = <V,>(was: Record<string, V>, id: string): Record<string, V> => {
    const { [id]: _gone, ...rest } = was;
    return rest;
  };
  const edit = (row: IntegrationRow) => {
    // What a test said was about the connection as it was.
    setTested((was) => without(was, row.id));
    setAdding((was) => ({ ...was, [row.kind]: false }));
    setRotating(null);
    setEditing(row.id);
  };
  const rotate = (row: IntegrationRow) => {
    setEditing(null);
    setAdding((was) => ({ ...was, webhook: false }));
    setRotating(row.id);
  };

  /**
   * One handler for both forms: a body carrying `kind` is a new connection, and a
   * body without one is a patch for the connection being edited. The discriminant is
   * the wire shape's own, so nothing here casts.
   */
  const submitted = (id: string | null) => async (body: NewIntegration | IntegrationPatch) => {
    if ("kind" in body) return void (await created(body));
    if (id === null || onUpdate === undefined) throw new Error("There is no connection to save to.");
    const saved = await onUpdate(id, body);
    if (saved) awaitCheck(saved.id);
  };

  /**
   * A save starts a check off the request path (#598), so its answer lands after the save has
   * returned. Waited for here, as a test's is, so it shows without a reload (#615); the row says
   * so when nothing arrives, or when what arrived could not be read.
   */
  const awaitCheck = (id: string) => {
    if (onAwaitCheck === undefined) return;
    setTested((was) => without(was, id));
    void onAwaitCheck(id).then(
      (answered) => {
        if (answered === null) setTested((was) => ({ ...was, [id]: CHECK_NOT_ANSWERED }));
      },
      (failed) => {
        const said = failed instanceof AnswerUnread ? unreadInWords("save", failed) : `${messageOf(failed)} ${CHECK_NOT_ANSWERED}`;
        setTested((was) => ({ ...was, [id]: said }));
      },
    );
  };

  /** A flow's save: the connection it made is waited for like an edited one. */
  const created = async (body: NewIntegration): Promise<IntegrationRow | void> => {
    const made = await onCreate(body);
    if (made) awaitCheck(made.id);
    return made;
  };

  /** A flow was refused beside a connection that exists (#653, #762): open that one's Edit. */
  const editInstead = (flow: AdapterKind) => (id: string) => {
    const existing = integrations?.find((each) => each.id === id && each.active);
    if (existing === undefined) {
      setFailure("That connection is not in the list on this page yet. Reload the page, then choose Edit on it.");
      return;
    }
    setFailure(null);
    close(flow);
    edit(existing);
  };

  const disconnect = async (id: string) => {
    setFailure(null);
    setBusy(true);
    try {
      await onDisable(id);
      setConfirming(null);
    } catch (refusal) {
      setFailure(`${messageOf(refusal)} The connection is still listed; retry.`);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Reconnect: the check runs first and the row goes live only if it passes (#820). The
   * outcome is said where a test's is, so a refusal shows its reason beside the button.
   */
  const enable = async (id: string) => {
    if (onEnable === undefined) return;
    setFailure(null);
    setEnabling((was) => ({ ...was, [id]: true }));
    setTested((was) => without(was, id));
    try {
      const answered = await onEnable(id);
      setTested((was) => ({ ...was, [id]: enabledInWords(answered) }));
    } catch (refusal) {
      setFailure(
        refusal instanceof AnswerUnread ? unreadInWords("enable", refusal) : `${messageOf(refusal)} The connection is still disconnected.`,
      );
    } finally {
      setEnabling((was) => without(was, id));
    }
  };

  const remove = async (id: string) => {
    if (onDelete === undefined) return;
    setFailure(null);
    setBusy(true);
    try {
      await onDelete(id);
      setConfirming(null);
      setTested((was) => without(was, id));
    } catch (refusal) {
      setFailure(`${messageOf(refusal)} The connection was not deleted.`);
    } finally {
      setBusy(false);
    }
  };

  // A test changes nothing, so it does not take the panel's `busy`: only that row's own
  // button waits, and a person can still edit or disconnect while Jira is being asked.
  const test = async (id: string) => {
    if (onTest === undefined) return;
    setFailure(null);
    setTesting((was) => ({ ...was, [id]: true }));
    setTested((was) => without(was, id));
    try {
      const answered = await onTest(id);
      setTested((was) => ({ ...was, [id]: testedInWords(answered) }));
    } catch (refusal) {
      // Asked for, and its answer not read, is not "not tested" (#615).
      setFailure(refusal instanceof AnswerUnread ? unreadInWords("test", refusal) : `${messageOf(refusal)} The connection was not tested.`);
    } finally {
      setTesting((was) => without(was, id));
    }
  };

  // A receiver's test event (#1076): like a test, it takes only its own row's button.
  const sendTestEvent = async (id: string) => {
    if (onSendTestEvent === undefined) return;
    setFailure(null);
    setSending((was) => ({ ...was, [id]: true }));
    setTested((was) => without(was, id));
    try {
      const answered = await onSendTestEvent(id);
      setTested((was) => ({ ...was, [id]: sentTestInWords(answered) }));
    } catch (refusal) {
      setFailure(refusal instanceof AnswerUnread ? unreadInWords("test", refusal) : `${messageOf(refusal)} No test event was sent.`);
    } finally {
      setSending((was) => without(was, id));
    }
  };

  return (
    <section className="panel" id="integrations" aria-label="Integrations" ref={section}>
      <div className="panel__head">
        <h2>Integrations</h2>
        {integrations === null ? null : <Chip>{active} active</Chip>}
      </div>
      <div className="panel__body stack stack--tight">
        {error === null ? null : <Notice tone="bad">{error}</Notice>}
        {failure === null ? null : <Notice tone="bad">{failure}</Notice>}
        {returned?.word === "replaced" ? (
          <Notice tone="good">{reinstalledInWords(heldBy(integrations, returned.installationId))}</Notice>
        ) : null}
        {integrations === null && error === null ? <p className="muted">Reading this workspace's integrations…</p> : null}
        {integrations !== null && integrations.length === 0 ? <p className="muted">No integration is connected.</p> : null}
      </div>
      {integrations !== null && integrations.length > 0 ? (
        <div className="table-wrap" tabIndex={0}>
          <table className="table integration-table" data-testid="integration-list">
            <thead>
              <tr>
                <th scope="col">Service</th>
                <th scope="col">Sends to</th>
                <th scope="col">Token</th>
                <th scope="col">Status</th>
                <th scope="col">Health</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {integrations.map((row) => (
                <tr key={row.id} data-testid={`row-${row.id}`}>
                  <td>
                    <strong>{KIND_LABEL[row.kind]}</strong>
                  </td>
                  <td>
                    <SendsTo row={row} />
                  </td>
                  <td>
                    <CredentialCell row={row} />
                  </td>
                  <td>
                    <Chip tone={statusOf(row).tone}>{statusOf(row).label}</Chip>
                  </td>
                  <td>
                    <Health
                      row={row}
                      deliveries={deliveries?.[row.id]}
                      onExpand={onExpand}
                      onRetry={mayManage && row.active && onRetry !== undefined ? (deliveryId) => onRetry(row.id, deliveryId) : undefined}
                      mayEdit={mayManage && onUpdate !== undefined}
                    />
                  </td>
                  <td>
                    {row.active && mayManage ? (
                      confirming === row.id ? (
                        <div className="stack stack--tight">
                          <small className="field__hint">{DISCONNECT_WARNING}</small>
                          <div className="actions">
                            <button type="button" className="btn btn--small btn--danger" disabled={busy} onClick={() => void disconnect(row.id)}>
                              Yes, disconnect
                            </button>
                            <button type="button" className="btn btn--small" disabled={busy} onClick={() => setConfirming(null)}>
                              Keep it
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="stack stack--tight">
                          <div className="actions">
                            {onTest === undefined || !row.has_credential || !TESTABLE.has(row.kind) ? null : (
                              <button type="button" className="btn btn--small" disabled={testing[row.id] === true} onClick={() => void test(row.id)}>
                                {testing[row.id] === true ? "Testing…" : "Test connection"}
                              </button>
                            )}
                            {onSendTestEvent === undefined || !row.has_credential || row.kind !== "webhook" ? null : (
                              <button
                                type="button"
                                className="btn btn--small"
                                disabled={sending[row.id] === true}
                                onClick={() => void sendTestEvent(row.id)}
                              >
                                {sending[row.id] === true ? "Sending…" : "Send test event"}
                              </button>
                            )}
                            {onUpdate === undefined ? null : (
                              <button type="button" className="btn btn--small" disabled={busy} onClick={() => edit(row)}>
                                Edit
                              </button>
                            )}
                            {row.kind !== "webhook" || onRotateWebhook === undefined || !row.has_credential ? null : (
                              <button
                                ref={(button) => {
                                  rotateButtons.current[row.id] = button;
                                }}
                                type="button"
                                className="btn btn--small"
                                disabled={busy}
                                onClick={() => rotate(row)}
                              >
                                Rotate secret
                              </button>
                            )}
                            {row.installation_id == null ? null : (
                              <a className="btn btn--small" href={reinstallHref(row.installation_id)}>
                                Reinstall
                              </a>
                            )}
                            <button type="button" className="btn btn--small btn--danger" disabled={busy} onClick={() => setConfirming(row.id)}>
                              Disconnect
                            </button>
                          </div>
                          {tested[row.id] === undefined ? null : (
                            <small className="field__hint" role="status" data-testid={`tested-${row.id}`}>
                              {tested[row.id]}
                            </small>
                          )}
                        </div>
                      )
                    ) : mayManage && (onEnable !== undefined || onDelete !== undefined) ? (
                      confirming === row.id ? (
                        <div className="stack stack--tight">
                          <small className="field__hint">{DELETE_WARNING}</small>
                          <div className="actions">
                            <button type="button" className="btn btn--small btn--danger" disabled={busy} onClick={() => void remove(row.id)}>
                              Yes, delete
                            </button>
                            <button type="button" className="btn btn--small" disabled={busy} onClick={() => setConfirming(null)}>
                              Keep it
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="stack stack--tight">
                          <div className="actions">
                            {onEnable === undefined || !row.has_credential || !RECONNECTABLE.has(row.kind) ? null : (
                              <button
                                type="button"
                                className="btn btn--small"
                                disabled={busy || enabling[row.id] === true}
                                onClick={() => void enable(row.id)}
                              >
                                {enabling[row.id] === true ? (row.kind === "webhook" ? "Sending test event…" : "Checking…") : "Reconnect"}
                              </button>
                            )}
                            {onDelete === undefined ? null : (
                              <button
                                type="button"
                                className="btn btn--small btn--danger"
                                disabled={busy || enabling[row.id] === true}
                                onClick={() => setConfirming(row.id)}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                          {tested[row.id] === undefined ? null : (
                            <small className="field__hint" role="status" data-testid={`tested-${row.id}`}>
                              {tested[row.id]}
                            </small>
                          )}
                        </div>
                      )
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {mayManage ? (
        <>
          {rotated === null || rotated.kind !== "webhook" || onRotateWebhook === undefined ? null : (
            <RotateWebhookSecret
              key={rotated.id}
              row={rotated}
              onRotate={onRotateWebhook}
              stepUpFactors={stepUpFactors}
              beginPasskey={beginPasskey}
              onClose={() => closeRotation(rotated.id)}
            />
          )}
          {edited === null ? null : edited.kind === "webhook" ? (
            <EditWebhook
              key={edited.id}
              row={edited}
              onUpdate={async (patch) => {
                await submitted(edited.id)(patch);
              }}
              onClose={() => setEditing(null)}
            />
          ) : edited.kind === "jira" ? (
            <JiraFlow
              key={edited.id}
              editing={edited}
              onCreate={created}
              onUpdate={submitted(edited.id)}
              onList={onListJira}
              onEdit={editInstead("jira")}
              onClose={() => setEditing(null)}
            />
          ) : (
            <SlackFlow
              key={edited.id}
              editing={edited}
              onCreate={created}
              onUpdate={submitted(edited.id)}
              onList={onListSlack}
              onEdit={editInstead("slack")}
              onClose={() => setEditing(null)}
            />
          )}
          {adding.jira && adding.slack && (adding.webhook || onRegisterWebhook === undefined) ? null : (
            <div className="panel__body actions">
              {adding.jira ? null : (
                <button type="button" className="btn" onClick={() => open("jira")}>
                  Connect Jira
                </button>
              )}
              {adding.slack ? null : (
                <button type="button" className="btn" onClick={() => open("slack")}>
                  Connect Slack
                </button>
              )}
              {adding.webhook || onRegisterWebhook === undefined ? null : (
                <button type="button" className="btn" onClick={() => open("webhook")}>
                  Connect webhook
                </button>
              )}
            </div>
          )}
          {adding.webhook && onRegisterWebhook !== undefined ? (
            <ConnectWebhook
              onRegister={onRegisterWebhook}
              stepUpFactors={stepUpFactors}
              beginPasskey={beginPasskey}
              onClose={() => close("webhook")}
            />
          ) : null}
          {adding.jira ? (
            <JiraFlow onCreate={created} onSendTest={onSendTest} onList={onListJira} onEdit={onUpdate === undefined ? undefined : editInstead("jira")} onClose={() => close("jira")} />
          ) : null}
          {adding.slack ? (
            <SlackFlow returned={returned}
              held={returned?.word === "already_installed" ? heldBy(integrations, returned.installationId) : undefined}
              onCreate={created}
              onSendTest={onSendTest}
              onList={onListSlack}
              onEdit={onUpdate === undefined ? undefined : editInstead("slack")}
              onClose={() => close("slack")}
            />
          ) : null}
        </>
      ) : (
        <p className="panel__body muted">Only a workspace admin can connect Jira, Slack or a webhook. Ask an admin in this workspace.</p>
      )}
    </section>
  );
}
